import React, { useState, useRef, useEffect } from 'react';
import {
    XMarkIcon,
    TruckIcon,
    Cog8ToothIcon,
    ArrowLeftIcon,
    DocumentArrowUpIcon,
    PaperAirplaneIcon,
    CpuChipIcon,
    SparklesIcon,
    Squares2X2Icon // Nuevo icono para el menú
} from '@heroicons/react/24/outline';
import { trailerTrucksResource, predictIAServices, upTempFile, getIALoads, chatAI } from '../../helpers/configRoutes/backend_helper';
import { useNavigate } from 'react-router-dom';
import ReactMarkdown from "react-markdown";
import clsx from "clsx";
import usePlanUsage, { PLAN_FEATURES, usageBlockedMessage } from '../../hooks/usePlanUsage';

const ChatBot = () => {
    const navigate = useNavigate();
    const fileInputRef = useRef(null);
    const messagesEndRef = useRef(null);

    const [isOpen, setIsOpen] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [trucksTrailers, setTrucksTrailer] = useState([]);
    
    // Estados de visibilidad
    const [showOptions, setShowOptions] = useState(false); // Para la lista de camiones
    const [showToolsMenu, setShowToolsMenu] = useState(true); // Para el menú de herramientas (Maintenance/Import)
    
    const [inputChat, setInputChat] = useState("");
    const [conversation_id, setConversation_id] = useState('');
    const [messages, setMessages] = useState([
        { role: 'assistant', content: 'Hello! I am your AI Logistics Assistant. How can I help you optimize your operation today?' }
    ]);
    const [showRespuesta, setShowRespuesta] = useState({ status: false, model: '', product_id: null });
    const { getFeature, canUse, refresh: refreshUsage } = usePlanUsage();
    const aiChatFeature = getFeature(PLAN_FEATURES.AI_CHAT);
    const aiLoadsFeature = getFeature(PLAN_FEATURES.AI_LOADS);
    const aiMaintenanceFeature = getFeature(PLAN_FEATURES.AI_MAINTENANCE);
    const canUseAiChat = canUse(PLAN_FEATURES.AI_CHAT);
    const canUseAiLoads = canUse(PLAN_FEATURES.AI_LOADS);
    const canUseAiMaintenance = canUse(PLAN_FEATURES.AI_MAINTENANCE);

    // Auto-scroll al fondo
    useEffect(() => {
        if (isOpen) {
            messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
        }
    }, [messages, isOpen, showRespuesta, showToolsMenu, showOptions]);

    // Ocultar herramientas automáticamente si el usuario interactúa
    useEffect(() => {
        if (messages.length > 1) {
            setShowToolsMenu(false);
        }
    }, [messages]);

    const toggleOpen = () => {
        if (isOpen) {
            setTimeout(() => {
                setShowOptions(false);
                setShowRespuesta({ status: false, model: '', product_id: null });
                setShowToolsMenu(true); // Resetear menú al cerrar
            }, 300);
        }
        setIsOpen((prev) => !prev);
    };

    const handleCreateServiceOrder = async () => {
        if (!canUseAiMaintenance) {
            setMessages(prev => [...prev, { role: 'assistant', content: usageBlockedMessage(aiMaintenanceFeature, 'solicitudes de IA de mantenimiento') }]);
            return;
        }
        setIsLoading(true);
        // Ocultamos el menú principal para mostrar opciones de camiones
        setShowToolsMenu(false); 
        try {
            const response = await trailerTrucksResource('get', { params: { truck: true } });
            setTrucksTrailer(response.items || []); 
            setShowOptions(true);
        } catch (error) {
            console.error("Error fetching trucks:", error);
            setMessages(prev => [...prev, { role: 'assistant', content: 'I could not retrieve the fleet list at this time.' }]);
        } finally {
            setIsLoading(false);
        }
    };

    const handleFileChange = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        if (!canUseAiLoads) {
            setMessages(prev => [...prev, { role: 'assistant', content: usageBlockedMessage(aiLoadsFeature, 'importaciones con IA') }]);
            e.target.value = '';
            return;
        }

        setShowToolsMenu(false);
        setShowRespuesta({ status: true, model: 'Loads', product_id: null });
        setShowOptions(false);

        try {
            const uploadResponse = await upTempFile({ file });

            if (!uploadResponse?.status) {
                throw new Error('Upload failed');
            }

            const response = await getIALoads({
                file_content: uploadResponse.content,
                file_type: uploadResponse.file_type,
                normalized_type: uploadResponse.normalized_type,
                content_kind: uploadResponse.content_kind,
                original_name: uploadResponse.original_name,
                document_metadata: uploadResponse.metadata,
            });

            if (!response?.status) {
                throw new Error(response?.message || 'AI processing failed');
            }

            await refreshUsage();
            setShowRespuesta({ status: true, model: 'Loads', product_id: response.id });
            setMessages(prev => [...prev, {
                role: 'assistant',
                content: `I detected ${response.loads_count || 0} load draft(s) from ${uploadResponse.original_name}. Review them in AI Operations.`
            }]);
        } catch (err) {
            setShowRespuesta({ status: false, model: '', product_id: null });
            setMessages(prev => [...prev, { role: 'assistant', content: 'Error processing file. Please try again with PDF, DOC/DOCX, Excel, CSV, or image files.' }]);
        } finally {
            e.target.value = '';
        }
    };

    const handleSelectTruck = async (truck) => {
        setShowRespuesta({ status: true, model: 'TrailerTruck', product_id: null });
        setShowOptions(false);
        try {
            const response = await predictIAServices({ truck: truck.id });
            await refreshUsage();
            setShowRespuesta({ status: true, model: 'TrailerTruck', product_id: response.id });
        } catch (err) {
            setShowRespuesta({ status: false, model: '', product_id: null });
            setMessages(prev => [...prev, { role: 'assistant', content: `Could not analyze truck ${truck.name}.` }]);
        }
    };

    const handleSendChat = async () => {
        if (!inputChat.trim() || isLoading) return;
        if (!canUseAiChat) {
            setMessages(prev => [...prev, { role: 'assistant', content: usageBlockedMessage(aiChatFeature, 'mensajes de IA') }]);
            return;
        }

        const currentMsg = inputChat;
        setInputChat("");
        setIsLoading(true);
        
        // Al enviar mensaje, aseguramos que el menú se oculte (por si estaba abierto manualmente)
        setShowToolsMenu(false);

        setMessages(prev => [...prev, { role: 'user', content: currentMsg }]);

        try {
            const response = await chatAI({
                message: currentMsg,
                token: conversation_id,
                detailed_message: 0
            });

            if (response.status && response.message_response?.message_agent) {
                setConversation_id(response.message_response.conversation_id);
                await refreshUsage();
                setMessages(prev => [
                    ...prev,
                    { role: 'assistant', content: response.message_response.message_agent }
                ]);
            } else {
                throw new Error("Invalid AI response");
            }

        } catch (err) {
            setMessages(prev => [
                ...prev,
                { role: 'assistant', content: 'I am having trouble connecting to the server. Please try again.' }
            ]);
        } finally {
            setIsLoading(false);
        }
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSendChat();
        }
    };

    return (
        <>
            <input type="file" ref={fileInputRef} className="hidden" onChange={handleFileChange} accept=".pdf,.doc,.docx,.txt,.rtf,.xlsx,.xls,.csv,.jpg,.jpeg,.png,.webp,.gif" />

            {/* BOTÓN FLOTANTE */}
            <button
                onClick={toggleOpen}
                className="fixed right-6 bottom-6 lg:right-8 lg:bottom-8 bg-cs-primary text-white p-4 rounded-2xl shadow-2xl hover:bg-cs-primary-hover transition-all z-50 flex items-center gap-3 active:scale-95 group"
            >
                <SparklesIcon className="w-6 h-6 animate-pulse" />
                <span className="font-bold text-sm hidden group-hover:block whitespace-nowrap">AI Assistant</span>
            </button>

            {/* PANEL LATERAL */}
            <div className={`fixed top-0 right-0 h-full w-[400px] bg-white shadow-[-20px_0_50px_rgba(0,0,0,0.1)] z-[60] transition-transform duration-500 ease-in-out transform ${isOpen ? 'translate-x-0' : 'translate-x-full'} flex flex-col`}>

                {/* HEADER */}
                <div className="bg-cs-primary-dark p-6 text-white flex items-center justify-between shrink-0 shadow-md z-10">
                    <div className="flex items-center gap-3">
                        <div className="p-2 bg-white/10 rounded-lg">
                            <CpuChipIcon className="w-6 h-6" />
                        </div>
                        <div>
                            <h2 className="font-bold text-base leading-none">Logistics AI</h2>
                            <p className="text-[10px] text-cs-primary-soft uppercase mt-1 font-bold">Enterprise Mode</p>
                        </div>
                    </div>
                    <div className="flex items-center gap-1">
                        {/* BOTÓN PARA RE-ACTIVAR TOOLS (Solo visible si no está ya visible) */}
                        {!showToolsMenu && !showOptions && !showRespuesta.status && (
                            <button 
                                onClick={() => setShowToolsMenu(true)} 
                                title="Show Tools"
                                className="p-2 hover:bg-white/10 rounded-full transition-colors animate-fadeIn"
                            >
                                <Squares2X2Icon className="w-6 h-6 text-white" />
                            </button>
                        )}
                        <button onClick={toggleOpen} className="p-2 hover:bg-white/10 rounded-full transition-colors">
                            <XMarkIcon className="w-6 h-6" />
                        </button>
                    </div>
                </div>

                {/* BODY */}
                <div className="flex-1 overflow-y-auto p-6 bg-cs-bg-alt relative custom-scrollbar flex flex-col">
                    {showRespuesta.status ? (
                        /* ESTADO DE PROCESAMIENTO IA */
                        <div className="flex flex-col items-center justify-center h-full text-center space-y-6 animate-fadeIn">
                            <div className="relative">
                                <div className="absolute inset-0 bg-cs-primary/20 blur-xl rounded-full animate-ping"></div>
                                <div className="relative bg-white p-6 rounded-3xl shadow-xl border border-cs-primary/20">
                                    <Cog8ToothIcon className="w-16 h-16 text-cs-primary animate-spin" />
                                </div>
                            </div>
                            <div>
                                <h3 className="text-cs-dark font-bold text-lg">AI is thinking...</h3>
                                <p className="text-cs-muted text-sm mt-2 px-4 italic">"I'm analyzing your data to provide the best logistical result."</p>
                            </div>
                            {showRespuesta.product_id && (
                                <button
                                    onClick={() => navigate(`/ia-${showRespuesta.model === 'TrailerTruck' ? 'trk' : 'loads'}/${showRespuesta.product_id}`)}
                                    className="bg-cs-primary text-white px-8 py-3 rounded-xl font-bold hover:shadow-lg transition-all"
                                >
                                    Review AI Result
                                </button>
                            )}
                        </div>
                    ) : (
                        <div className="flex flex-col flex-1">
                            {/* MENSAJES DEL CHAT */}
                            <div className="space-y-4 flex-1">
                                {messages.map((msg, i) => (
                                    <div
                                        key={i}
                                        className={clsx("flex", {
                                            "justify-end": msg.role === "user",
                                            "justify-start": msg.role !== "user",
                                        })}
                                    >
                                        <div
                                            className={clsx(
                                                "max-w-[85%] p-4 rounded-2xl text-sm shadow whitespace-normal break-words",
                                                {
                                                    "bg-cs-primary text-white shadow-md rounded-br-none": msg.role === "user",
                                                    "bg-white border border-cs-border text-cs-dark shadow-sm rounded-bl-none": msg.role !== "user",
                                                }
                                            )}
                                        >
                                            {msg.role === "user" ? (
                                                <span>{msg.content}</span>
                                            ) : (
                                                <div className="prose prose-sm max-w-none text-current">
                                                    <ReactMarkdown>{msg.content}</ReactMarkdown>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                ))}
                                {isLoading && (
                                     <div className="flex justify-start">
                                         <div className="bg-white border border-cs-border p-3 rounded-2xl rounded-bl-none shadow-sm flex gap-1">
                                             <div className="w-2 h-2 bg-cs-muted rounded-full animate-bounce"></div>
                                             <div className="w-2 h-2 bg-cs-muted rounded-full animate-bounce delay-75"></div>
                                             <div className="w-2 h-2 bg-cs-muted rounded-full animate-bounce delay-150"></div>
                                         </div>
                                     </div>
                                )}
                                <div ref={messagesEndRef} />
                            </div>

                            {/* SELECTOR DE TRUCKS (Cubre el área inferior si está activo) */}
                            {showOptions && (
                                <div className="mt-4 space-y-3 animate-fadeIn bg-cs-bg-alt pt-2 border-t border-cs-border/50">
                                    <div className="flex items-center gap-2 mb-2">
                                        <button onClick={() => setShowOptions(false)} className="text-cs-muted hover:text-cs-dark p-1 rounded-md hover:bg-gray-100"><ArrowLeftIcon className="w-4 h-4" /></button>
                                        <p className="text-[10px] font-bold text-cs-muted uppercase">Select a unit to analyze</p>
                                    </div>
                                    <div className="grid grid-cols-1 gap-2 max-h-[250px] overflow-y-auto custom-scrollbar">
                                        {trucksTrailers.length > 0 ? trucksTrailers.map((t, i) => (
                                            <div
                                                key={i}
                                                onClick={() => handleSelectTruck(t)}
                                                className="p-3 bg-white border border-cs-border rounded-xl hover:border-cs-primary transition-all cursor-pointer flex justify-between items-center group"
                                            >
                                                <div>
                                                    <p className="text-sm font-bold text-cs-dark">{t.name}</p>
                                                    <p className="text-[10px] text-cs-muted">VIN: {t.vin}</p>
                                                </div>
                                                <TruckIcon className="w-5 h-5 text-cs-border group-hover:text-cs-primary" />
                                            </div>
                                        )) : (
                                            <p className="text-sm text-cs-muted text-center py-4">No trucks found.</p>
                                        )}
                                    </div>
                                </div>
                            )}

                            {/* TOOLS / HERRAMIENTAS (Se muestran solo si showToolsMenu es true) */}
                            {!showOptions && showToolsMenu && (
                                <div className="grid grid-cols-1 gap-3 pt-4 mt-4 border-t border-cs-border animate-slideUp">
                                    <div className="flex justify-between items-center px-1">
                                        <p className="text-[10px] font-bold text-cs-muted uppercase tracking-widest">Available Tools</p>
                                        {/* Botón X pequeño para cerrar menú manualmente si ya hay mensajes */}
                                        {messages.length > 1 && (
                                            <button onClick={() => setShowToolsMenu(false)} className="text-cs-muted hover:text-cs-dark">
                                                <XMarkIcon className="w-4 h-4" />
                                            </button>
                                        )}
                                    </div>
                                    <button
                                        onClick={handleCreateServiceOrder}
                                        disabled={isLoading || !canUseAiMaintenance}
                                        title={!canUseAiMaintenance ? usageBlockedMessage(aiMaintenanceFeature, 'solicitudes de IA de mantenimiento') : ''}
                                        className="flex items-center gap-4 p-4 bg-white border border-cs-border rounded-2xl hover:border-cs-primary hover:bg-cs-primary-soft transition-all text-left disabled:opacity-50 disabled:cursor-not-allowed group"
                                    >
                                        <div className="p-2 bg-cs-primary-soft text-cs-primary rounded-xl group-hover:scale-110 transition-transform"><SparklesIcon className="w-6 h-6" /></div>
                                        <div>
                                            <p className="text-sm font-bold text-cs-dark leading-none">Maintenance AI</p>
                                            <p className="text-[10px] text-cs-muted mt-1">Predict services based on history</p>
                                        </div>
                                    </button>

                                    <button
                                        onClick={() => canUseAiLoads && fileInputRef.current.click()}
                                        disabled={!canUseAiLoads}
                                        title={!canUseAiLoads ? usageBlockedMessage(aiLoadsFeature, 'importaciones con IA') : ''}
                                        className="flex items-center gap-4 p-4 bg-white border border-cs-border rounded-2xl hover:border-cs-primary hover:bg-cs-primary-soft transition-all text-left disabled:opacity-50 disabled:cursor-not-allowed group"
                                    >
                                        <div className="p-2 bg-cs-surface-alt text-cs-primary rounded-xl group-hover:scale-110 transition-transform"><DocumentArrowUpIcon className="w-6 h-6" /></div>
                                        <div>
                                            <p className="text-sm font-bold text-cs-dark leading-none">Auto-Load Import</p>
                                            <p className="text-[10px] text-cs-muted mt-1">Extract loads from PDF/Excel</p>
                                        </div>
                                    </button>
                                </div>
                            )}
                        </div>
                    )}
                </div>

                {/* FOOTER - CHAT INPUT */}
                <div className="p-6 bg-white border-t border-cs-border shrink-0 z-20">
                    <div className="relative flex items-center">
                        <input
                            type="text"
                            value={inputChat}
                            onChange={(e) => setInputChat(e.target.value)}
                            onKeyDown={handleKeyDown}
                            disabled={isLoading || showRespuesta.status || !canUseAiChat}
                            placeholder={!canUseAiChat ? usageBlockedMessage(aiChatFeature, 'mensajes de IA') : (showRespuesta.status ? "AI is processing..." : "Ask me anything...")}
                            className="w-full pl-4 pr-12 py-3 bg-cs-bg-alt border border-cs-border rounded-xl focus:outline-none focus:border-cs-primary transition-all text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                        />
                        <button
                            onClick={handleSendChat}
                            disabled={!inputChat.trim() || isLoading || !canUseAiChat}
                            className="absolute right-2 p-2 text-cs-primary hover:bg-cs-primary-soft rounded-lg transition-all disabled:opacity-30 disabled:hover:bg-transparent"
                        >
                            <PaperAirplaneIcon className="w-5 h-5" />
                        </button>
                    </div>
                </div>
            </div>
        </>
    );
};

export default ChatBot;